import { getFileUrl } from '@/clients/http'
import { Pencil, RefreshCw, Trash2, User } from 'lucide-react'
import { FormProvider } from 'react-hook-form'
import { Link } from 'react-router-dom'

import { useCustomersTable } from '@/hooks/features/customers'
import { useDataTable } from '@/hooks/ui'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { ColumnVisibilityDropdown, DataTable, NoData } from '@/components/ui/data-table'
import { Pagination } from '@/components/ui/pagination-controls'
import { Switch } from '@/components/ui/switch'

import { ActionDropdown, ConfirmDialog, PageHeader, StatsCards, StatusBadge } from '@/components/atoms'

import { CustomerModel } from '@/types'

import { CustomerDialog } from './CustomerDialog'
import { CustomersFilters } from './CustomersFilters'
import { Button } from '@/components/ui'

type TableColumns = 'customer' | 'email' | 'phone' | 'status' | 'is_blocked' | 'actions'

const DEFAULT_VISIBLE_COLUMNS: TableColumns[] = [
  'customer',
  'email',
  'phone',
  'status',
  'is_blocked',
  'actions',
]

export const CustomersTable = () => {
  const {
    loading,
    data,
    pagination,
    formMethods,
    stats,
    deleteDialogOpen,
    customerToDelete,
    deleteLoading,
    createLoading,
    updateLoading,
    handleCreateCustomer,
    handleUpdateCustomer,
    mode,
    customerDialogOpen,
    editCustomer,
    openCreateDialog,
    openEditDialog,
    openViewDialog,
    closeDialog,
    setPage,
    setPageSize,
    handleDeleteClick,
    handleDeleteConfirm,
    setDeleteDialogOpen,
    toggleBlock,
    refresh
  } = useCustomersTable()

  const { columns, changeColumnVisibility, disabledColumns } = useDataTable<
    CustomerModel.Customer,
    TableColumns
  >({
    columns: [
      {
        accessorKey: 'customer',
        columnLabel: () => 'Customer',
        cell: (data) => (
          <div className="flex items-center gap-3">
            <Avatar>
              {data.image?.path ? (
                <AvatarImage src={getFileUrl(data.image.path)} alt={data.name} />
              ) : (
                <AvatarImage
                  src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${data.email || data.name}`}
                />
              )}
              <AvatarFallback>
                {data.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .substring(0, 2)
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <Link
              to={`/customers/${data._id}`}
              className="text-sm font-medium text-ink hover:underline"
            >
              {data.name}
            </Link>
          </div>
        ),
        hasSorting: true,
      },
      {
        accessorKey: 'email',
        columnLabel: () => 'Email',
        cell: (data) => <span className="text-text-2">{data.email}</span>,
      },
      {
        accessorKey: 'phone',
        columnLabel: () => 'Phone',
        cell: (data) => <span className="tabular-nums text-text-2">{data.phone || 'N/A'}</span>,
      },
      {
        accessorKey: 'status',
        columnLabel: () => 'Status',
        cell: (data) => (
          <StatusBadge type="user" status={data.status} />
        ),
      },
      {
        accessorKey: 'is_blocked',
        columnLabel: () => 'Blocked',
        cell: (data) => (
          <div className="flex items-center gap-2">
            <StatusBadge type="user" status={data.is_blocked ? 'Yes' : 'No'} />
            <Switch
              checked={data.is_blocked}
              onCheckedChange={() => toggleBlock(data)}
            />
          </div>
        ),
      },
      {
        accessorKey: 'actions',
        columnLabel: () => 'Actions',
        cell: (data) => (
          <div className="flex justify-end">
            <ActionDropdown
              items={[
                {
                  label: 'View',
                  icon: User,
                  onClick: () => openViewDialog(data),
                },
                {
                  label: 'Edit',
                  icon: Pencil,
                  onClick: () => openEditDialog(data),
                },
                {
                  label: 'Delete',
                  icon: Trash2,
                  variant: 'destructive',
                  onClick: () => handleDeleteClick(data),
                },
              ]}
            />
          </div>
        ),
        classes: () => ({
          column: 'text-right',
          cell: 'text-right',
        }),
        isAction: true,
      },
    ],
    visibleColumns: DEFAULT_VISIBLE_COLUMNS,
    disabledColumns: ['actions'],
  })

  return (
    <div className="space-y-4">
      <PageHeader
        onAddClick={openCreateDialog}
        title="Customers"
        description="Manage your customer base and relationships."
        addButtonText="Add customer"
      />

      <StatsCards
        stats={[
          {
            title: 'Total customers',
            value: stats.totalCustomers,
            icon: User,
          },
          {
            title: 'Active customers',
            value: stats.activeCustomers,
          }
        ]}
      />

      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col justify-between gap-3 lg:flex-row lg:items-center">
            <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
              <Pagination.PageSizeDropdown
                pageSize={pagination.pageSize}
                onPageSizeChange={setPageSize}
              />

              <FormProvider {...formMethods}>
                <CustomersFilters />
              </FormProvider>
            </div>

            <div className="order-first flex items-center justify-end gap-2 lg:order-last">
              <Button variant="outline" size="icon" onClick={refresh} disabled={loading} aria-label="Refresh">
                <RefreshCw className={loading ? 'animate-spin' : undefined} />
              </Button>
              <ColumnVisibilityDropdown
                columns={columns}
                onColumnVisibilityChange={changeColumnVisibility}
                disabledColumns={disabledColumns}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!loading && data.length === 0 ? (
            <NoData
              openCreateDialog={openCreateDialog}
              btnText="Add customer"
              title="No customers found"
            />
          ) : (
            <>
              <DataTable columns={columns} data={data} loading={loading} />

              <Pagination.Buttons
                currentPage={pagination.currentPage}
                totalItems={pagination.totalCount}
                pageSize={pagination.pageSize}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete customer?"
        description={`This will permanently delete the customer "${customerToDelete?.name}". This action cannot be undone.`}
        confirmText={deleteLoading ? 'Deleting…' : 'Delete customer'}
        onConfirm={handleDeleteConfirm}
        loading={deleteLoading}
        variant="destructive"
      />

      <CustomerDialog
        open={customerDialogOpen}
        mode={mode}
        editCustomer={editCustomer}
        closeDialog={closeDialog}
        onCreate={handleCreateCustomer}
        onUpdate={handleUpdateCustomer}
        loading={createLoading || updateLoading}
      />
    </div>
  )
}
